'use client';
import Link from 'next/link';
import { Box, Stack } from '@mui/material';
import { PALETTE } from '@/styles/constants';
import Logo from '@/assets/icons/leap-logo.svg';

export interface NavLinkItem {
  href: string;
  label: string;
  external?: boolean;
}

interface NavProps {
  links?: NavLinkItem[];
  accountHref?: string;
  accountLabel?: string;
}

export default function Nav({ links = [], accountHref, accountLabel = 'Account' }: NavProps) {
  return (
    <Box
      component="nav"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: { xs: '22px 24px', sm: '30px 60px' },
        background: PALETTE.forest,
        borderBottom: '1px solid rgba(198, 168, 98, 0.25)',
      }}
    >
      <Box
        component={Link}
        href="https://www.leaphei.com"
        aria-label="Leap home"
        sx={{
          display: 'block',
          width: '62px',
          height: '30px',
          overflow: 'hidden',
          position: 'relative',
          transition: 'transform 0.25s ease',
          '&:hover': { transform: 'scale(1.08)' },
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            transform: 'scale(0.5)',
            transformOrigin: 'top left',
          }}
        >
          <Logo />
        </Box>
      </Box>
      <Stack
        component="ul"
        direction="row"
        alignItems="center"
        sx={{ gap: { xs: '20px', sm: '40px' }, listStyle: 'none' }}
      >
        {links.map((link) => (
          <Box component="li" key={link.href} sx={{ display: { xs: 'none', sm: 'block' } }}>
            <Box
              component={Link}
              href={link.href}
              target={link.external ? '_blank' : undefined}
              rel={link.external ? 'noopener noreferrer' : undefined}
              sx={{
                display: 'inline-block',
                color: 'rgba(243, 236, 220, 0.72)',
                textDecoration: 'none',
                fontSize: '12.5px',
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                position: 'relative',
                paddingBottom: '3px',
                transition: 'color 0.25s ease, transform 0.25s ease',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  bottom: 0,
                  height: '1px',
                  background: PALETTE.goldLine,
                  transform: 'scaleX(0)',
                  transformOrigin: 'left',
                  transition: 'transform 0.3s ease',
                },
                '&:hover': {
                  color: PALETTE.parchment,
                  transform: 'scale(1.08)',
                },
                '&:hover::after': { transform: 'scaleX(1)' },
              }}
            >
              {link.label}
            </Box>
          </Box>
        ))}
        {accountHref && (
          <Box component="li">
            <Box
              component={Link}
              href={accountHref}
              sx={{
                display: 'inline-block',
                textDecoration: 'none',
                fontSize: '11.5px',
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                border: `1px solid ${PALETTE.goldSoft}`,
                color: `${PALETTE.goldSoft} !important`,
                padding: '9px 20px',
                transition: 'transform 0.25s ease, background 0.25s ease, color 0.25s ease',
                '&:hover': {
                  background: PALETTE.goldSoft,
                  color: `${PALETTE.forest} !important`,
                  transform: 'scale(1.06)',
                },
              }}
            >
              {accountLabel}
            </Box>
          </Box>
        )}
      </Stack>
    </Box>
  );
}
